// src/utils/filterHelpers.ts
// Utilities for filter serialization, persistence, and conversion

import {
  EntityFilters,
  FilterFieldType,
  FilterSchema,
  ENTITY_FILTER_SCHEMA,
  DEFAULT_ENTITY_FILTERS,
} from "@/types/filters";
import { parseOptions } from "@/utils/parserHelpers";

// ============================================
// Constants
// ============================================

const STORAGE_KEY_BASE = "eventsmagic_entity_filters";

/**
 * Build the localStorage key for filter persistence.
 * When userId is provided, the key is scoped per user so that
 * different users on the same device get independent filter state.
 * Falls back to the legacy unscoped key when userId is not available.
 */
const getStorageKey = (userId?: string | null): string => {
  return userId ? `${STORAGE_KEY_BASE}_${userId}` : STORAGE_KEY_BASE;
};

// ============================================
// URL Serialization
// ============================================

/**
 * Serialize a filter value to URL string format
 */
export const serializeFilterValue = (
  value: unknown,
  type: FilterFieldType
): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  switch (type) {
    case "string":
      return String(value) || null;

    case "string[]":
      if (Array.isArray(value) && value.length > 0) {
        return value.join(",");
      }
      return null;

    case "date":
      if (typeof value === "string" && value) {
        return value;
      }
      return null;

    case "number":
      if (typeof value === "number" && !isNaN(value)) {
        return value.toString();
      }
      return null;

    default:
      return null;
  }
};

/**
 * Deserialize a URL string to typed filter value
 */
export const deserializeFilterValue = (
  value: string | null,
  type: FilterFieldType
): unknown => {
  if (value === null || value === undefined || value === "") {
    return type === "string[]" ? [] : null;
  }

  switch (type) {
    case "string":
      return value;

    case "string[]":
      return value.split(",").filter((v) => v.trim() !== "");

    case "date":
      // Validate date format (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }
      return null;

    case "number": {
      const parsed = parseInt(value, 10);

      return isNaN(parsed) ? null : parsed;
    }

    default:
      return null;
  }
};

/**
 * Parse filters from URL search params
 * Only extracts fields defined in current schema - unknown params are ignored
 * This handles schema changes gracefully (removed fields won't cause issues)
 */
export const parseFiltersFromUrl = (
  searchParams: URLSearchParams,
  schema: FilterSchema = ENTITY_FILTER_SCHEMA
): Partial<EntityFilters> => {
  const filters: Partial<EntityFilters> = {};

  // Only iterate over known schema fields - unknown URL params are ignored
  for (const [key, fieldSchema] of Object.entries(schema)) {
    const filterKey = key as keyof EntityFilters;
    const urlValue = searchParams.get(fieldSchema.urlParam);
    const deserializedValue = deserializeFilterValue(
      urlValue,
      fieldSchema.type
    );

    // Only include if different from default
    if (
      deserializedValue !== null &&
      deserializedValue !== fieldSchema.defaultValue
    ) {
      // Handle arrays separately
      if (fieldSchema.type === "string[]") {
        const arr = deserializedValue as string[];
        if (arr.length > 0) {
          // Type-safe assignment using type assertion
          (filters[filterKey] as unknown) = arr;
        }
      } else {
        (filters[filterKey] as unknown) = deserializedValue;
      }
    }
  }

  return filters;
};

/**
 * Build URL search params from filters
 */
export const buildSearchParams = (
  filters: Partial<EntityFilters>,
  schema: FilterSchema = ENTITY_FILTER_SCHEMA
): URLSearchParams => {
  const params = new URLSearchParams();

  for (const [key, fieldSchema] of Object.entries(schema)) {
    const filterKey = key as keyof EntityFilters;
    const value = filters[filterKey];
    const serialized = serializeFilterValue(value, fieldSchema.type);

    if (serialized !== null) {
      params.set(fieldSchema.urlParam, serialized);
    }
  }

  return params;
};

/**
 * Build a URL path with filters as search params
 */
export const buildUrlWithFilters = (
  basePath: string,
  filters: Partial<EntityFilters>,
  schema: FilterSchema = ENTITY_FILTER_SCHEMA
): string => {
  const params = buildSearchParams(filters, schema);
  const queryString = params.toString();

  if (queryString) {
    return `${basePath}?${queryString}`;
  }

  return basePath;
};

/**
 * Merge current search params with new filters
 * Preserves any non-filter params that might be in the URL
 */
export const mergeFiltersWithSearchParams = (
  currentParams: URLSearchParams,
  filters: Partial<EntityFilters>,
  schema: FilterSchema = ENTITY_FILTER_SCHEMA
): URLSearchParams => {
  const newParams = new URLSearchParams(currentParams);

  // First, remove all filter params
  for (const fieldSchema of Object.values(schema)) {
    newParams.delete(fieldSchema.urlParam);
  }

  // Then add the new filter values
  for (const [key, fieldSchema] of Object.entries(schema)) {
    const filterKey = key as keyof EntityFilters;
    const value = filters[filterKey];
    const serialized = serializeFilterValue(value, fieldSchema.type);

    if (serialized !== null) {
      newParams.set(fieldSchema.urlParam, serialized);
    }
  }

  return newParams;
};

// ============================================
// localStorage Persistence
// ============================================

/**
 * Migrate filters from the legacy unscoped storage key to
 * the user-scoped key. Called once during loadFiltersFromStorage
 * when a userId is provided but no user-scoped entry exists yet.
 *
 * After migration the legacy key is removed so the next user
 * logging in on the same device starts with a clean slate.
 */
const migrateFromLegacyStorage = (userId: string): Partial<EntityFilters> | null => {
  try {
    const legacyKey = STORAGE_KEY_BASE;
    const legacyRaw = localStorage.getItem(legacyKey);

    if (!legacyRaw) {
      return null;
    }

    // Move legacy data to user-scoped key
    const userKey = getStorageKey(userId);
    localStorage.setItem(userKey, legacyRaw);

    // Remove legacy key
    localStorage.removeItem(legacyKey);

    return JSON.parse(legacyRaw) as Partial<EntityFilters>;
  } catch {
    return null;
  }
};

/**
 * Save filters to localStorage, scoped by userId.
 *
 * @param filters - The filters to persist
 * @param userId  - Current user ID for per-user storage scoping.
 *                  When null/undefined, falls back to the legacy unscoped key.
 */
export const saveFiltersToStorage = (
  filters: Partial<EntityFilters>,
  userId?: string | null
): void => {
  try {
    const storageKey = getStorageKey(userId);

    // Only save non-default values
    const toSave: Partial<EntityFilters> = {};

    for (const [key, value] of Object.entries(filters)) {
      const filterKey = key as keyof EntityFilters;
      const schema = ENTITY_FILTER_SCHEMA[filterKey];
      if (schema && value !== schema.defaultValue) {
        // For arrays, check if empty
        if (schema.type === "string[]") {
          if (Array.isArray(value) && value.length > 0) {
            (toSave[filterKey] as unknown) = value;
          }
        } else if (value !== null && value !== undefined) {
          (toSave[filterKey] as unknown) = value;
        }
      }
    }

    if (Object.keys(toSave).length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(toSave));
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch (error) {
    console.warn("Failed to save filters to localStorage:", error);
  }
};

/**
 * Load filters from localStorage, scoped by userId.
 *
 * Resolution order when userId is provided:
 * 1. User-scoped key (`eventsmagic_entity_filters_{userId}`)
 * 2. Legacy unscoped key (`eventsmagic_entity_filters`) — migrated to scoped key if found
 * 3. Empty object (no stored filters)
 *
 * @param userId - Current user ID for per-user storage scoping.
 *                 When null/undefined, reads from the legacy unscoped key.
 */
export const loadFiltersFromStorage = (
  userId?: string | null
): Partial<EntityFilters> => {
  try {
    const storageKey = getStorageKey(userId);
    let stored = localStorage.getItem(storageKey);

    // If no user-scoped entry exists but userId is provided,
    // try to migrate from the legacy unscoped key
    if (!stored && userId) {
      const migrated = migrateFromLegacyStorage(userId);
      if (migrated) {
        // migrateFromLegacyStorage already saved to the scoped key,
        // so re-read from storage to go through the normal parse path
        stored = localStorage.getItem(storageKey);
      }
    }

    if (!stored) {
      return {};
    }

    // Use parseOptions for safe parsing with defaults
    // This handles schema changes gracefully - new fields get defaults, removed fields are ignored
    const parsed = parseOptions(stored, DEFAULT_ENTITY_FILTERS);

    // Sanitize and validate loaded values against current schema
    const filters: Partial<EntityFilters> = {};

    for (const [key, schema] of Object.entries(ENTITY_FILTER_SCHEMA)) {
      const filterKey = key as keyof EntityFilters;
      const value = parsed[filterKey];

      // Skip if value matches default (no need to include)
      if (value === schema.defaultValue) {
        continue;
      }

      // Validate based on type
      if (schema.type === "string" && typeof value === "string" && value) {
        (filters[filterKey] as unknown) = value;
      } else if (schema.type === "string[]" && Array.isArray(value)) {
        const validArray = value.filter(
          (v: unknown) => typeof v === "string" && v
        );
        if (validArray.length > 0) {
          (filters[filterKey] as unknown) = validArray;
        }
      } else if (schema.type === "date" && typeof value === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          (filters[filterKey] as unknown) = value;
        }
      } else if (schema.type === "number" && typeof value === "number") {
        if (!isNaN(value)) {
          (filters[filterKey] as unknown) = value;
        }
      }
    }

    return filters;
  } catch (error) {
    console.warn("Failed to load filters from localStorage:", error);
    return {};
  }
};

/**
 * Clear filters from localStorage for the given user.
 *
 * @param userId - Current user ID. When provided, clears the user-scoped key.
 *                 When null/undefined, clears the legacy unscoped key.
 */
export const clearFiltersFromStorage = (
  userId?: string | null
): void => {
  try {
    localStorage.removeItem(getStorageKey(userId));
  } catch (error) {
    console.warn("Failed to clear filters from localStorage:", error);
  }
};

// ============================================
// Utility Functions
// ============================================

/**
 * Check if filters have any active (non-default) values
 */
export const hasActiveFilters = (filters: EntityFilters): boolean => {
  return (
    filters.dateFrom !== null ||
    filters.dateTo !== null ||
    filters.tagIds.length > 0 ||
    filters.year !== null
  );
};

/**
 * Merge partial filters with defaults
 */
export const mergeWithDefaults = (
  partial: Partial<EntityFilters>
): EntityFilters => {
  return {
    ...DEFAULT_ENTITY_FILTERS,
    ...partial,
    // Ensure arrays are not undefined
    tagIds: partial.tagIds || DEFAULT_ENTITY_FILTERS.tagIds,
  };
};

/**
 * Compare two filter objects for equality
 */
export const areFiltersEqual = (
  a: EntityFilters,
  b: EntityFilters
): boolean => {
  return (
    a.dateFrom === b.dateFrom &&
    a.dateTo === b.dateTo &&
    a.year === b.year &&
    a.tagIds.length === b.tagIds.length &&
    a.tagIds.every((id, index) => id === b.tagIds[index])
  );
};